import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Mailbox } from 'lucide-react';
import dateFnsLocales from '@utils/dateFnsLocales';
import { useBranding } from '@utils/api';
import { useGuidedBookingContext } from '@contexts/GuidedBookingContext';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { TriangleAlert } from 'lucide-react';
import { GuidedBookingErrorCode } from '@/components/SharedTypes';

const CompletionStep: FC = () => {
  const { t, i18n } = useTranslation();
  const { branding } = useBranding();
  const { bookingData, isSubmitting, submissionErrorCode, retrySubmission } = useGuidedBookingContext();

  if (!branding) return null;

  // Show loading state while submitting
  if (isSubmitting) {
    return (
      <div className="w-3/4 mx-auto">
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: branding.accentColor }}></div>
          <p className="text-lg font-medium">{t('booking.submitting')}</p>
          <p className="text-sm text-gray-600">{t('booking.submittingMessage')}</p>
        </div>
      </div>
    );
  }

  // Show error state if submission failed
  if (submissionErrorCode) {
    // Check if this is a token already used error using errorCode
    const isTokenAlreadyUsed = submissionErrorCode === GuidedBookingErrorCode.TOKEN_ALREADY_USED || submissionErrorCode === GuidedBookingErrorCode.NO_TOKEN;
    
    // Get error message based on error code
    const getErrorMessage = () => {
      switch (submissionErrorCode) {
        case GuidedBookingErrorCode.NO_TOKEN:
          return t('guidedBooking.errors.noToken');
        case GuidedBookingErrorCode.TOKEN_ALREADY_USED:
          return t('guidedBooking.errors.tokenAlreadyUsed');
        case GuidedBookingErrorCode.VALIDATION_ERROR:
          return t('guidedBooking.errors.validationError');
        case GuidedBookingErrorCode.GENERAL_ERROR:
        default:
          return t('guidedBooking.errors.submissionErrorMessage');
      }
    };
    
    return (
      <div className="w-3/4 mx-auto">
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <div className="text-red-500 text-xl"><TriangleAlert /></div>
          <p className="text-lg font-medium text-red-600">
            {isTokenAlreadyUsed 
              ? t('guidedBooking.errors.tokenExpired')
              : t('guidedBooking.errors.submissionError')
            }
          </p>
          <p className="text-sm text-gray-600 text-center">{getErrorMessage()}</p>
          {!isTokenAlreadyUsed && (
            <Button 
              onClick={retrySubmission}
              disabled={isSubmitting}
              style={{ backgroundColor: branding.buttonColor }}
              className="hover:shadow-lg transition-all hover:-translate-y-px"
            >
              {t('booking.buttons.retry')}
            </Button>
          )}
          {isTokenAlreadyUsed && (
            <p className="text-sm text-gray-500 text-center">
              {t('guidedBooking.errors.requestNewLink')}
            </p>
          )}
        </div>
      </div>
    );
  }

  const scheduledAt = bookingData.scheduledAt;

  return (
    <div className="w-3/4 mx-auto">
      <div className="flex justify-center">
        <Mailbox color={branding.accentColor} size={36}/>
      </div>
      <div className="pt-2 pb-4">
        <p className="text-base">{t('success.appointmentTitle')}</p>
        <p className="text-lg font-medium">
          {t('success.appointmentDateTime', {
            day: format(parseISO(scheduledAt), "ccccccc, MMM dd yyyy", { 
              locale: dateFnsLocales[i18n.language] 
            }), 
            time: format(parseISO(scheduledAt), "h:mm a", { 
              locale: dateFnsLocales[i18n.language] 
            })
          })}
        </p>
        <p className="text-base">{t('success.appointmentFooter')}</p>
      </div>
      <Separator/>
      <p className="p-4 text-lg">
        {t('success.nextSteps', {clinicName: branding.name})}
      </p>
      <Separator/>
      <p className="p-4 text-sm">{t('success.closePage')}</p>
    </div>
  );
};

export default CompletionStep;